import { useRef, useEffect, useMemo, useCallback, PropsWithChildren } from 'react';
import ArticleOutlinedIcon from '@mui/icons-material/ArticleOutlined';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import BoltOutlinedIcon from '@mui/icons-material/BoltOutlined';
import BugReportOutlinedIcon from '@mui/icons-material/BugReportOutlined';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import InsightsIcon from '@mui/icons-material/InsightsOutlined';
import LinkIcon from '@mui/icons-material/LinkOutlined';
import LogoutIcon from '@mui/icons-material/Logout';
import ManageSearchOutlinedIcon from '@mui/icons-material/ManageSearchOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import TrendingUpOutlinedIcon from '@mui/icons-material/TrendingUpOutlined';
import { Box } from '@mui/material';
import { useLocation } from 'react-router-dom';
import useBreakpoint from '@/hooks/useBreakpoint';
import { useOrgStack } from '@/hooks/ceox/useOrgStack';
import useExport from '@/hooks/useExport';
import useLogout from '@/hooks/useLogout';
import useProfile from '@/hooks/useProfileV2';
import usePullToRefresh from '@/hooks/usePullToRefresh';
import usePwaInstall from '@/hooks/usePwaInstall';
import usePwaUpdate from '@/hooks/usePwaUpdate';
import useScrollToTop from '@/hooks/useScrollToTop';
import { getAppMenu } from '@/routes/config';
import { dropdownMenus } from '@/routes/menu';
import { getAccessibleServices, getServiceByPath, getCeoxSidebarMenus } from '@/services/serviceRegistry';
import Header from '@/trio-ui/Header';
import InstallBanner from '@/trio-ui/InstallBanner';
import MobileNavigation from '@/trio-ui/MobileNavigation';
import MobilePageTransition from '@/trio-ui/MobilePageTransition';
import PullToRefreshIndicator from '@/trio-ui/PullToRefreshIndicator';
import Sidebar from '@/trio-ui/Sidebar';
import UpdateBanner from '@/trio-ui/UpdateBanner';
import { UserMenuOptionType } from '@/trio-ui/UserMenuDropDown/UserMenuDropDown.component';
import { AppModules, AvailableRoles } from '@/types';
import { Role } from '@/types/api';
import { SidebarOptionType } from '@/types/routes';
import styles from './PageLayout.styles';

type PageLayoutComponentProps = PropsWithChildren;

/** Extract projectId from SEOx project paths like /seox/projects/:projectId/... */
function extractSeoProjectId(pathname: string): string | null {
  const match = pathname.match(/^\/seox\/projects\/([^/]+)/);
  return match ? match[1] : null;
}

/** Build dynamic sidebar items for an SEOx project */
function buildSeoProjectSidebar(projectId: string): SidebarOptionType[] {
  const base = `/seox/projects/${projectId}`;
  return [
    { path: base, label: 'Overview', icon: TrendingUpOutlinedIcon },
    { path: `${base}/audits`, label: 'Site Audit', icon: BugReportOutlinedIcon },
    { path: `${base}/keywords`, label: 'Keywords', icon: ManageSearchOutlinedIcon },
    { path: `${base}/pages`, label: 'Pages', icon: ArticleOutlinedIcon },
    { path: `${base}/links`, label: 'Internal Links', icon: LinkIcon },
    { path: `${base}/copilot`, label: 'Copilot', icon: AutoFixHighIcon },
    { path: `${base}/tasks`, label: 'Tasks', icon: AssignmentOutlinedIcon },
    { path: `${base}/automation`, label: 'Automation', icon: BoltOutlinedIcon },
    { path: `${base}/changelog`, label: 'Changelog', icon: HistoryOutlinedIcon },
    { path: `${base}/content`, label: 'Content', icon: DescriptionOutlinedIcon },
    { path: `${base}/reports`, label: 'Reports', icon: InsightsIcon },
    { path: `${base}/settings`, label: 'Settings', icon: SettingsOutlinedIcon },
  ];
}

const PageLayoutComponent = function ({ children }: PageLayoutComponentProps): JSX.Element {
  const { updateContent } = useExport();
  const { profile } = useProfile();
  const { roles, username, initials, picture, isNotInvited } = profile;
  const { pathname } = useLocation();
  const { isMobile } = useBreakpoint();
  const { canInstall, isDismissed, promptInstall, dismiss } = usePwaInstall();
  const { updateAvailable, refresh } = usePwaUpdate();

  useScrollToTop();

  const handlePullRefresh = useCallback(async () => {
    // Reload the current page data by re-triggering React Query invalidation
    window.location.reload();
  }, []);

  const { isRefreshing, pullDistance } = usePullToRefresh({
    onRefresh: handlePullRefresh,
    enabled: isMobile,
  });

  const onLogoutHandler = useLogout();

  const accessibleServices = useMemo(() => getAccessibleServices(roles as Role[]), [roles]);

  const currentService = useMemo(() => getServiceByPath(pathname), [pathname]);

  // Business stack for dynamic CEOx sidebar
  const { stack: ceoxStack } = useOrgStack();

  const sidebarOptions = useMemo(() => {
    if (!roles) return [];

    const isSEOx = currentService?.id === AppModules.SEOX;
    const isCEOx = currentService?.id === AppModules.CEOX;
    const seoProjectId = isSEOx ? extractSeoProjectId(pathname) : null;

    // Use dynamic sidebar for SEOx project pages or CEOx stack-aware sidebar
    let sidebarMenus: SidebarOptionType[];
    if (seoProjectId) {
      sidebarMenus = buildSeoProjectSidebar(seoProjectId);
    } else if (isCEOx) {
      sidebarMenus = getCeoxSidebarMenus(ceoxStack);
    } else {
      sidebarMenus = currentService?.sidebarMenus ?? [];
    }

    let userPermissions: string[] = [];
    (roles as Role[]).forEach((role) => {
      const rolePermissionValues = role.permissions.map((p) => p.value);
      userPermissions = [...userPermissions, ...rolePermissionValues];
    });

    const isSettings = currentService?.id === AppModules.SETTINGS;
    const isFinanceX = currentService?.id === AppModules.FINANCEX;
    const allowedMenus = sidebarMenus.filter((menu) => {
      if (isSEOx || isCEOx || isSettings || isFinanceX) return true;
      const artifact = `web:${menu.path}`;
      const legacyPath = menu.path
        .replace('/talentx/', '/hiring/')
        .replace('/peoplex/', '/people/');
      const legacyArtifact = `web:${legacyPath}`;
      return userPermissions.includes(artifact) || userPermissions.includes(legacyArtifact);
    });

    const sidebarMenuActivedIndex = allowedMenus.findIndex((menu) => {
      // For SEOx project overview (base path), exact match
      if (seoProjectId && menu.path === `/seox/projects/${seoProjectId}`) {
        return pathname === menu.path;
      }
      // Exact match for module base paths
      if (menu.path === currentService?.basePath) return pathname === menu.path;
      return pathname === menu.path || pathname.includes(menu.path);
    });

    return allowedMenus.map((menu, index) => ({
      ...menu,
      isSelected: sidebarMenuActivedIndex === index,
    }));
  }, [pathname, currentService, roles, ceoxStack]);

  // Dropdown menus (Settings) — used for user menu
  const dropdownMenuOptions = useMemo(() => {
    const filteredHeaderMenu = getAppMenu(dropdownMenus, roles as Role[]);
    const isPeopleAdminUser = roles?.find(
      (role: Role) => role.value === AvailableRoles.PEOPLE_ADMIN
    );
    return filteredHeaderMenu
      .filter((option) => !option.adminOnly || isPeopleAdminUser)
      .map((option) => ({
        ...option,
        path: option.sidebarMenus[0]?.path || option.path,
      }));
  }, [roles]);

  const parentComponentRef = useRef(null);

  useEffect(() => {
    if (parentComponentRef.current) {
      updateContent(parentComponentRef);
    }
  }, [updateContent]);

  const containsOnPathname = useCallback(
    (module: string): boolean => pathname.includes(module),
    [pathname]
  );

  const containerStyles = [styles.container, styles.grayBackground];

  const userMenuDropDownOptions: UserMenuOptionType[] = [
    ...dropdownMenuOptions.map(({ icon: Icon, label, path }) => ({
      label,
      icon: <Icon />,
      href: path,
    })),
    { label: 'Log Out', icon: <LogoutIcon />, handleClickOnOption: onLogoutHandler },
  ];

  const ignorePadding = ['/home'].some(containsOnPathname);

  const pageStyles = [styles.page, ...(!ignorePadding ? [styles.pagePadding] : [styles.childrenn])];

  return (
    <>
      <div id="dialog-container" />
      <Box ref={parentComponentRef} sx={containerStyles}>
        <Header
          currentService={currentService}
          accessibleServices={accessibleServices}
          hideAvatar={!!isNotInvited}
          username={username ?? ''}
          initials={initials ?? ''}
          picture={picture ?? ''}
          userMenuDropDownOptions={userMenuDropDownOptions}
        />
        <Box sx={styles.content}>
          {!isMobile && <Sidebar sidebarOptions={sidebarOptions} />}
          <Box
            sx={[
              ...(Array.isArray(pageStyles) ? pageStyles : [pageStyles]),
              ...(isMobile ? [styles.mobilePagePadding] : []),
            ]}
            id="page-content"
          >
            {isMobile ? <MobilePageTransition>{children}</MobilePageTransition> : children}
          </Box>
        </Box>
      </Box>
      {isMobile && (
        <MobileNavigation
          sidebarOptions={sidebarOptions}
          currentService={currentService}
          accessibleServices={accessibleServices}
        />
      )}
      {isMobile && (
        <PullToRefreshIndicator
          pullDistance={pullDistance}
          isRefreshing={isRefreshing}
          threshold={80}
        />
      )}
      {isMobile && canInstall && !isDismissed && (
        <InstallBanner onInstall={promptInstall} onDismiss={dismiss} />
      )}
      {updateAvailable && <UpdateBanner onRefresh={refresh} />}
    </>
  );
};

export default PageLayoutComponent;
